import React, { useMemo } from 'react'
import { useFragment, graphql } from 'react-relay'

import { ViewProps, getTokenValue, Token, styled, View } from 'tamagui'

import { Image } from '../Image'
import { Icon } from '../Icon'

import { GuildPrimaryPhotoNew_guild$key } from './__generated__/GuildPrimaryPhotoNew_guild.graphql'

const GuildDefaultFrame = styled(View, {
  name: 'GuildDefaultPhoto',
  backgroundColor: '$background',

  borderWidth: 1,
  borderColor: '$borderColor',

  variants: {
    size: {
      '...size': (size, { tokens }) => {
        const sizeToken = tokens.size[size]

        if (
          typeof sizeToken === 'object' &&
          'val' in sizeToken &&
          sizeToken.val > 40
        ) {
          return {
            boxShadow: '0 0 0 3px var(--background)',
            borderRadius: '$4',
          }
        }

        return {
          boxShadow: '0 0 0 1px var(--background)',
          borderRadius: '$2',
        }
      },
    },
  } as const,

  defaultVariants: {
    size: '$5',
  },
} as const)

const GuildPrimaryPhotoFrame = styled(Image, {
  name: 'GuildPrimaryPhoto',
  backgroundColor: '$background',

  borderWidth: 1,
  borderColor: '$borderColor',

  variants: {
    size: {
      '...size': (size, { tokens }) => {
        const sizeToken = tokens.size[size]

        if (
          typeof sizeToken === 'object' &&
          'val' in sizeToken &&
          sizeToken.val > 40
        ) {
          return {
            boxShadow: '0 0 0 3px var(--background)',
            borderRadius: '$4',
          }
        }

        return {
          boxShadow: '0 0 0 1px var(--background)',
          borderRadius: '$2',
        }
      },
    },
  } as const,

  defaultVariants: {
    size: '$5',
  },
} as const)

export const GuildPrimaryPhoto = GuildPrimaryPhotoFrame.styleable<
  HTMLImageElement,
  {
    guild: GuildPrimaryPhotoNew_guild$key
    size: Token
  } & ViewProps
>(({ guild, size = '$5', ...props }, ref) => {
  const data = useFragment(
    graphql`
      fragment GuildPrimaryPhotoNew_guild on Guild
      @argumentDefinitions(
        imageSize: { type: "Int", defaultValue: 100 }
        dpr: { type: "Int", defaultValue: 3 }
      ) {
        name
        rowId

        primaryPhoto {
          transformUrl(
            transform: { w: $imageSize, h: $imageSize, dpr: $dpr, fit: "cover" }
          )
        }
      }
    `,
    guild
  )

  const parsedSize = useMemo(() => {
    return getTokenValue(size, 'size')
  }, [size])

  if (!data.primaryPhoto?.transformUrl) {
    return (
      <GuildDefaultFrame
        {...props}
        ref={ref}
        size={size}
        width={parsedSize}
        height={parsedSize}
        alignItems='center'
        justifyContent='center'
      >
        <Icon
          name='home-filled'
          width={parsedSize / 2}
          height={parsedSize / 2}
        />
      </GuildDefaultFrame>
    )
  }

  return (
    <GuildPrimaryPhotoFrame
      {...(props as any)}
      alt={`Primary Photo for ${data.name}`}
      ref={ref}
      size={size}
      src={data.primaryPhoto.transformUrl}
      width={parsedSize}
      height={parsedSize}
    />
  )
})
